<template>
  <div class="medhop-data-table">
    <div v-if="showSummary" class="medhop-data-table__summary">
      <div class="medhop-data-table__summary-main">
        <span class="medhop-data-table__summary-icon">
          <i :class="summaryIcon" aria-hidden="true"></i>
        </span>
        <div>
          <strong>{{ summaryTitle }}</strong>
          <small>{{ visibleRows }} baris ditampilkan pada halaman ini</small>
        </div>
      </div>
      <slot name="summary-actions"></slot>
    </div>

    <b-table
      v-bind="$attrs"
      v-on="$listeners"
      class="medhop-data-table__table"
      :items="items"
      :fields="fields"
      :responsive="responsive"
      :show-empty="showEmpty"
      :empty-text="emptyText"
      :empty-filtered-text="emptyFilteredText"
    >
      <template
        v-for="(_, slotName) in forwardedSlots"
        v-slot:[slotName]="slotData"
      >
        <slot :name="slotName" v-bind="slotData" />
      </template>
    </b-table>
  </div>
</template>

<script>
export default {
  name: "MedhopDataTable",
  inheritAttrs: false,
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    fields: {
      type: Array,
      default: () => [],
    },
    summaryTitle: {
      type: String,
      default: "Data",
    },
    showSummary: {
      type: Boolean,
      default: true,
    },
    responsive: {
      type: [Boolean, String],
      default: true,
    },
    showEmpty: {
      type: Boolean,
      default: true,
    },
    emptyText: {
      type: String,
      default: "Belum ada data untuk ditampilkan",
    },
    emptyFilteredText: {
      type: String,
      default: "Data yang dicari tidak ditemukan",
    },
  },
  computed: {
    visibleRows() {
      return this.items.length
    },
    summaryIcon() {
      const title = this.summaryTitle.toLowerCase()
      const icons = [
        [/obat|farmasi|alkes/, "fas fa-pills"],
        [/pasien|medical check/, "fas fa-user-injured"],
        [/dokter|pengobatan/, "fas fa-user-md"],
        [/perjanjian|jadwal|kalender/, "far fa-calendar-check"],
        [/pembayaran|invoice|deposit/, "fas fa-receipt"],
        [/inventori|stok|stock|gudang/, "fas fa-boxes"],
        [/laboratorium|\blab\b/, "fas fa-flask"],
        [/bangsal|tempat tidur/, "fas fa-bed"],
        [/darah|donor/, "fas fa-tint"],
        [/tindakan|rekam medis/, "fas fa-stethoscope"],
        [/laporan|rekap/, "fas fa-chart-bar"],
        [/penjualan/, "fas fa-shopping-cart"],
        [/pengeluaran/, "fas fa-file-invoice-dollar"],
        [/resep/, "fas fa-prescription"],
        [/surat pesanan|\bpo\b/, "fas fa-file-signature"],
        [/ruangan|gedung/, "far fa-building"],
        [/supplier|vendor/, "fas fa-truck"],
      ]
      const match = icons.find(([pattern]) => pattern.test(title))
      return match ? match[1] : "fas fa-list-alt"
    },
    forwardedSlots() {
      const slots = { ...this.$scopedSlots }
      delete slots["summary-actions"]
      return slots
    },
  },
}
</script>

<style lang="scss">
.medhop-data-table {
  width: 100%;
  margin-top: 0.8rem;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  background: #fff;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(20, 50, 99, 0.055);
}

.medhop-data-table__summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 56px;
  padding: 0.65rem 0.85rem;
  border-bottom: 1px solid #e5edf4;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbfd 100%);
}

.medhop-data-table__summary-main {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.medhop-data-table__summary-main strong,
.medhop-data-table__summary-main small {
  display: block;
}

.medhop-data-table__summary-main strong {
  margin-bottom: 1px;
  color: #29465f;
  font-size: 15px;
  font-weight: 750;
  line-height: 1.35;
}

.medhop-data-table__summary-main small {
  color: #8292a6;
  font-size: 11.5px;
  line-height: 1.4;
}

.medhop-data-table__summary-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  color: #0f7f86;
  background: #eaf7f6;
}

.medhop-data-table__summary-icon i { color: #0f7f86 !important; }

.medhop-data-table .table-responsive {
  margin: 0 !important;
  border: 0 !important;
  border-radius: 0 !important;
}

.medhop-data-table .table,
.medhop-data-table table {
  width: 100%;
  margin-bottom: 0 !important;
}

.medhop-data-table .table thead th,
.medhop-data-table thead th {
  padding: 0.8rem 0.75rem !important;
  border-top: 0 !important;
  border-bottom: 1px solid #e7edf4 !important;
  background: #f1f6f9 !important;
  color: #415d73 !important;
  font-size: 0.72rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.025em;
  vertical-align: middle !important;
  white-space: nowrap;
}

.medhop-data-table .table tbody td,
.medhop-data-table tbody td {
  padding: 0.75rem !important;
  border-top-color: #edf2f7 !important;
  color: #526174;
  font-size: 0.82rem;
  vertical-align: middle !important;
}

.medhop-data-table .table-striped tbody tr:nth-of-type(odd) td {
  background: #fbfcfe;
}

.medhop-data-table .table-hover tbody tr:hover td {
  background: rgba(30, 149, 143, 0.045);
}

.medhop-data-table .b-table-empty-row td {
  height: 112px;
  color: #94a3b8 !important;
  text-align: center;
}

.medhop-data-table .btn-sm {
  min-width: 30px;
  min-height: 30px;
  border-radius: 8px !important;
}

@media (max-width: 767.98px) {
  .medhop-data-table { border-radius: 10px; }
  .medhop-data-table__summary { min-height: 52px; padding: 0.55rem 0.65rem; }
  .medhop-data-table .table thead th,
  .medhop-data-table .table tbody td { padding: 0.65rem !important; }
}
</style>
